import type { ReactNode } from 'react';
import { Checkbox } from '@shared/components';
import type { GroupPermissionTriple, SecurityGroupRecord } from '../types/security-admin.types';

interface Props {
  groups: SecurityGroupRecord[];
  selectedIds: Set<string>;
  onToggleGroup: (id: string, checked: boolean) => void;
  defaults: GroupPermissionTriple;
  onToggleDefault: (key: keyof GroupPermissionTriple, value: boolean) => void;
  maxGroups: number;
  emptyMessage: ReactNode;
}

/**
 * Group checklist + shared default-permissions checkboxes, reused by
 * AssignUserToGroupsModal and the create-user inline picker.
 */
export function SecurityGroupPermissionsPicker({
  groups,
  selectedIds,
  onToggleGroup,
  defaults,
  onToggleDefault,
  maxGroups,
  emptyMessage,
}: Props) {
  const overCap = selectedIds.size > maxGroups;
  const atCap = selectedIds.size >= maxGroups;

  if (groups.length === 0) {
    return <p className="text-mp-muted">{emptyMessage}</p>;
  }

  return (
    <>
      <div className="text-sm text-mp-muted">
        Selected: {selectedIds.size} / {maxGroups}
        {overCap && <span className="text-mp-error ml-2">Cannot select more than {maxGroups}.</span>}
      </div>

      <ul className="max-h-64 overflow-y-auto space-y-1">
        {groups.map((g) => {
          const checked = selectedIds.has(g.id);
          return (
            <li key={g.id}>
              <Checkbox
                id={`assign-group-${g.id}`}
                checked={checked}
                onChange={(next) => onToggleGroup(g.id, next)}
                label={g.name}
                disabled={atCap && !checked}
              />
            </li>
          );
        })}
      </ul>

      <section className="border-t border-mp-border pt-3 space-y-2">
        <h3 className="text-sm font-semibold">Default permissions (optional)</h3>
        <p className="text-xs text-mp-muted">
          Sets the group-level permissions for this user in each new group. Per-dealer
          overrides can be set later.
        </p>
        <p className="text-xs text-mp-muted">
          Any of these lets the member see the agreements, quotes and customers inside this group's reach. Can quote saves quotes, Can sell completes them, and Can invoice raises invoices against agreements in the reach.
        </p>
        <Checkbox
          id="defaults-can-quote"
          checked={defaults.can_quote}
          onChange={(v) => onToggleDefault('can_quote', v)}
          label="Can quote"
        />
        <Checkbox
          id="defaults-can-invoice"
          checked={defaults.can_invoice}
          onChange={(v) => onToggleDefault('can_invoice', v)}
          label="Can invoice"
        />
        <Checkbox
          id="defaults-can-sell"
          checked={defaults.can_sell}
          onChange={(v) => onToggleDefault('can_sell', v)}
          label="Can sell"
        />
      </section>
    </>
  );
}
